import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Send, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMO_IMAGE = "https://customer-assets.emergentagent.com/job_8776705b-3965-48ce-aa62-68a5febfba36/artifacts/elyzowla_daily%20skin%20tips%202026.png";

const DailyTipsPromo = () => {
  const { language } = useLanguage();
  const isFr = language === "fr";

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [frequency, setFrequency] = useState("daily"); // daily | weekly | biweekly
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubscribing(true);
    setSubscribeError("");

    try {
      const res = await axios.post(`${API}/daily-tips/subscribe`, {
        email: email.trim(),
        first_name: firstName.trim(),
        frequency,
      });

      if (res.data.success) {
        setSubscribeSuccess(true);
        setEmail("");
        setFirstName("");
      } else {
        setSubscribeError(res.data.message || "Subscription failed");
      }
    } catch {
      setSubscribeError(
        isFr
          ? "Une erreur est survenue. Veuillez réessayer."
          : "Something went wrong. Please try again."
      );
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="rounded-2xl overflow-hidden shadow-xl border border-amber-200/60 bg-white"
      data-testid="daily-tips-promo"
    >
      {/* Promotional Image */}
      <div className="w-full">
        <img
          src={PROMO_IMAGE}
          alt={isFr ? "Karine's Insider Tips — trucs et conseils sur tous nos services" : "Karine's Insider Tips — expert advice across every service"}
          className="w-full h-auto object-cover"
          data-testid="daily-tips-promo-image"
        />
      </div>

      {/* Signup Box */}
      <div className="px-5 sm:px-8 py-6 sm:py-8 bg-gradient-to-b from-[#fdf6ee] to-[#faf0e4]">
        {subscribeSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-4"
            data-testid="daily-tips-promo-success"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Check className="w-7 h-7 text-white" />
            </div>
            <h3
              className="font-bold text-xl text-gray-900 mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {isFr ? "Bienvenue dans la famille!" : "Welcome to the Family!"}
            </h3>
            <p className="text-gray-600 text-sm">
              {isFr
                ? "Votre premier conseil Insider de Karine arrivera bientôt à 7h!"
                : "Your first Karine's Insider Tip arrives soon at 7am!"}
            </p>
          </motion.div>
        ) : (
          <>
            <div className="text-center mb-5">
              <h3
                className="text-xl sm:text-2xl font-bold text-gray-900 mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="daily-tips-promo-heading"
              >
                {isFr
                  ? "Karine's Insider Tips"
                  : "Karine's Insider Tips"}
              </h3>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed max-w-lg mx-auto">
                {isFr
                  ? "Inscrivez-vous pour recevoir les conseils Insider de Karine — Botox, fillers, HydraFacial, laser et plus — livrés gratuitement à votre boîte courriel à 7h."
                  : "Sign up to receive Karine's Insider Tips — Botox, fillers, HydraFacial, laser & more — delivered free to your inbox at 7am. Expert clinical knowledge across every service we offer."}
              </p>
            </div>

            <form
              onSubmit={handleSubscribe}
              className="max-w-md mx-auto space-y-3"
              data-testid="daily-tips-promo-form"
            >
              <input
                type="text"
                placeholder={isFr ? "Prenom (optionnel)" : "First name (optional)"}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-amber-200 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
                data-testid="daily-tips-promo-firstname"
              />
              <input
                type="email"
                placeholder={isFr ? "Votre adresse courriel" : "Your email address"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-amber-200 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
                data-testid="daily-tips-promo-email"
              />

              {/* Frequency picker — how often the client wants to receive tips */}
              <div>
                <p className="text-xs text-gray-600 font-medium mb-2 text-center uppercase tracking-wider">
                  {isFr ? "Fréquence de livraison" : "How often?"}
                </p>
                <div className="grid grid-cols-3 gap-2" role="radiogroup">
                  {[
                    { value: "daily",    en: "Daily",    fr: "Quotidien"  },
                    { value: "weekly",   en: "Weekly",   fr: "Hebdo"      },
                    { value: "biweekly", en: "Bi-weekly",fr: "Aux 2 sem." },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={frequency === opt.value}
                      onClick={() => setFrequency(opt.value)}
                      data-testid={`daily-tips-promo-freq-${opt.value}`}
                      className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                        frequency === opt.value
                          ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white border-transparent shadow-md"
                          : "bg-white text-gray-700 border-amber-200 hover:border-amber-400"
                      }`}
                    >
                      {isFr ? opt.fr : opt.en}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubscribing || !email}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold text-sm hover:from-amber-600 hover:to-orange-600 transition-all shadow-md hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="daily-tips-promo-submit"
              >
                {isSubscribing ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    {isFr ? "S'inscrire — C'est Gratuit!" : "Subscribe — It's Free!"}
                  </>
                )}
              </button>

              {subscribeError && (
                <p className="text-red-500 text-xs text-center" data-testid="daily-tips-promo-error">
                  {subscribeError}
                </p>
              )}
            </form>

            <p className="text-xs text-gray-400 mt-4 text-center">
              {isFr
                ? "Livraison gratuite chaque matin a 7h. Desabonnement facile."
                : "Free delivery every morning at 7am. Unsubscribe anytime."}
            </p>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default DailyTipsPromo;
